// CMS Payments Page
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCMS } from './CMSContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Search, ChevronLeft, ChevronRight, CreditCard, Calendar, 
  CheckCircle, XCircle, Clock, Eye, RefreshCw, Download, RotateCcw, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { getStatusMeta } from '../lib/bookingStatus';

// Resolve a lucide-react icon component from the name string in the status meta.
const STATUS_ICONS = { CheckCircle, XCircle, Clock, RotateCcw, AlertTriangle };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CMSPayments = () => {
  const { getAuthHeaders, hasRole } = useCMS();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [refundDialog, setRefundDialog] = useState({ open: false, payment: null });
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refunding, setRefunding] = useState(false);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const res = await axios.get(`${API}/cms/payments?${params.toString()}`, { 
        headers: getAuthHeaders() 
      });
      setPayments(res.data.payments);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [page, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPayments();
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const res = await axios.get(`${API}/cms/payments/export?${params.toString()}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payments_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleRefund = async () => {
    if (!refundReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setRefunding(true);
    try {
      const body = { reason: refundReason };
      if (refundAmount && parseFloat(refundAmount) > 0) {
        body.amount = parseFloat(refundAmount);
      }
      
      await axios.post(
        `${API}/cms/payments/${refundDialog.payment.payment_id}/refund`, 
        body,
        { headers: getAuthHeaders() }
      );
      toast.success('Refund processed successfully');
      setRefundDialog({ open: false, payment: null });
      setRefundReason('');
      setRefundAmount('');
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const meta = getStatusMeta(status);
    const Icon = STATUS_ICONS[meta.icon] || Clock;
    return (
      <Badge className={`${meta.badge} hover:${meta.badge}`}>
        <Icon className="w-3 h-3 mr-1" />
        {meta.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payments</h1>
          <p className="text-slate-400">{total} total payments</p>
        </div>
        <div className="flex gap-2">
          {hasRole('super_admin', 'finance') && (
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
          <Button variant="outline" onClick={fetchPayments} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search by payment ID or user..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-slate-900 border-slate-600"
                />
              </div>
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-600">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-40 bg-slate-900 border-slate-600"
              placeholder="From"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-40 bg-slate-900 border-slate-600"
              placeholder="To"
            />
            <Button type="submit">Search</Button>
          </form>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-700 bg-slate-900/50">
                <tr>
                  <th className="text-left p-4 text-slate-400 font-medium">Payment ID</th>
                  <th className="text-left p-4 text-slate-400 font-medium">User</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Amount</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Date</th>
                  <th className="text-right p-4 text-slate-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : payments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400">
                      No payments found
                    </td>
                  </tr>
                ) : (
                  payments.map((payment) => (
                    <tr key={payment.payment_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="p-4">
                        <p className="font-mono text-sm">{payment.payment_id?.slice(0, 12)}...</p>
                        {payment.stripe_payment_intent && (
                          <p className="text-xs text-slate-500 font-mono">
                            {payment.stripe_payment_intent.slice(0, 20)}...
                          </p>
                        )}
                      </td>
                      <td className="p-4">
                        <p className="text-sm">{payment.user_email || payment.user_id?.slice(0, 12)}</p>
                      </td>
                      <td className="p-4">
                        <p className="font-bold">€{payment.amount?.toFixed(2)}</p>
                        {payment.pass_type && (
                          <p className="text-xs text-slate-400">{payment.pass_type}</p>
                        )}
                      </td>
                      <td className="p-4">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="p-4 text-slate-400 text-sm">
                        {formatDate(payment.created_at)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {payment.user_id && (
                            <Link to={`/cms/users/${payment.user_id}`}>
                              <Button variant="ghost" size="sm">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                          )}
                          {hasRole('super_admin', 'finance') && payment.status === 'paid' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRefundDialog({ open: true, payment })}
                            >
                              <RotateCcw className="w-4 h-4 text-yellow-400" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Refund Dialog */}
      <Dialog open={refundDialog.open} onOpenChange={(open) => setRefundDialog({ ...refundDialog, open })}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader>
            <DialogTitle>Process Refund</DialogTitle>
          </DialogHeader>
          {refundDialog.payment && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-900 rounded-lg">
                <p className="text-sm text-slate-400">Payment Amount</p>
                <p className="text-2xl font-bold text-gray-900">€{refundDialog.payment.amount?.toFixed(2)}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Refund Amount (leave empty for full refund)</Label>
                <Input
                  type="number"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  max={refundDialog.payment.amount}
                  step="0.01"
                  placeholder={`Max: €${refundDialog.payment.amount?.toFixed(2)}`}
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Reason *</Label>
                <Textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Enter reason for refund..."
                  className="bg-white border-gray-300 text-gray-900"
                  required
                />
              </div>
              <Button onClick={handleRefund} className="w-full" disabled={refunding}>
                {refunding ? 'Processing...' : 'Process Refund'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSPayments;
